// The compta sidebar (docs/projet/interface/README.md), built INTO Dust's
// navigation architecture rather than beside it: same container, same
// contexts, same user menu — but the entries are the nine compta pages, the
// conversations list is gone, and the rail collapses to icons instead of
// disappearing.

import "@app/components/shell/shell.css";
import "@app/components/shell/shell.css";

import { TrialMessageUsage } from "@app/components/app/TrialMessageUsage";
import type {
  ComptaNavEntry,
  SidebarNavigation,
} from "@app/components/navigation/config";
import {
  getAdminSectionHref,
  getComptaNavigation,
} from "@app/components/navigation/config";
import { useDesktopNavigation } from "@app/components/navigation/DesktopNavigationContext";
import { SidebarUserMenu } from "@app/components/navigation/SidebarUserMenu";
import { SidebarContext } from "@app/components/sparkle/SidebarContext";
import { useAuth, useFeatureFlags } from "@app/lib/auth/AuthContext";
import { SIDEBAR_BRAND_NAME } from "@app/lib/compta/branding";
import { FREE_TRIAL_PHONE_PLAN_CODE } from "@app/lib/plans/plan_codes";
import { useAppRouter } from "@app/lib/platform";
import { useWorkspacePermissions } from "@app/lib/swr/permissions";
import { hasGroupManagementScope } from "@app/types/api/auth_context";
import type { SubscriptionType } from "@app/types/plan";
import type { UserTypeWithWorkspaces, WorkspaceType } from "@app/types/user";
import { isAdmin } from "@app/types/user";
import { cn, LayoutLeft, XClose } from "@dust-tt/sparkle";
import React, { useContext, useMemo } from "react";

interface NavigationSidebarProps {
  owner: WorkspaceType;
  subNavigation?: SidebarNavigation[] | null;
  // TODO(2024-06-19 flav) Move subscription to a hook.
  subscription: SubscriptionType;
  user: (UserTypeWithWorkspaces & { subscriberHash?: string | null }) | null;
  isMobile?: boolean;
}

interface RailEntryProps {
  entry: ComptaNavEntry;
  isCollapsed: boolean;
}

function RailEntry({ entry, isCollapsed }: RailEntryProps) {
  const Icon = entry.icon;
  return (
    <a
      href={entry.href}
      className={cn(
        "compta-rail__navi",
        entry.current && "compta-rail__navi--active"
      )}
      aria-current={entry.current ? "page" : undefined}
      title={isCollapsed ? entry.label : undefined}
    >
      <Icon />
      {!isCollapsed && entry.label}
    </a>
  );
}

export const NavigationSidebar = React.forwardRef<
  HTMLDivElement,
  NavigationSidebarProps
>(function NavigationSidebar(
  {
    owner,
    subscription,
    subNavigation,
    user,
    isMobile,
  }: NavigationSidebarProps,
  ref
) {
  const router = useAppRouter();
  const activePath = useMemo(() => {
    if (router.isReady && router.pathname) {
      return router.pathname;
    }
    return "";
  }, [router.isReady, router.pathname]);

  const { hasFeature } = useFeatureFlags();
  const { groupManagement } = useAuth();
  const { hasPermission } = useWorkspacePermissions();

  const hasManagedGroups = hasGroupManagementScope(groupManagement?.read_usage);
  const adminSectionHref = getAdminSectionHref(
    owner,
    hasPermission,
    hasManagedGroups
  );

  const groups = useMemo(
    () => getComptaNavigation(owner, activePath, adminSectionHref),
    [owner, activePath, adminSectionHref]
  );

  const { setSidebarOpen } = useContext(SidebarContext);
  const { isNavigationBarOpen, setIsNavigationBarOpen } =
    useDesktopNavigation();

  // On desktop the rail collapses to icons; the mobile sheet is always full.
  const isCollapsed = !isMobile && !isNavigationBarOpen;

  return (
    <div
      ref={ref}
      className={cn(
        "compta-rail flex min-w-0 grow flex-col",
        isCollapsed && "compta-rail--collapsed"
      )}
    >
      <div className="compta-rail__brand">
        <span className="compta-rail__brand-mark">
          {SIDEBAR_BRAND_NAME.charAt(0)}
        </span>
        {!isCollapsed && SIDEBAR_BRAND_NAME}
        {isMobile ? (
          <button
            type="button"
            className="compta-rail__toggle"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fermer la navigation"
          >
            <XClose className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            className="compta-rail__toggle"
            onClick={() => setIsNavigationBarOpen(!isNavigationBarOpen)}
            aria-label={
              isCollapsed ? "Déplier la navigation" : "Replier la navigation"
            }
            title={isCollapsed ? "Déplier" : "Replier"}
          >
            <LayoutLeft className="h-4 w-4" />
          </button>
        )}
      </div>

      {groups.map((group) => (
        <React.Fragment key={group.id}>
          <div className="compta-rail__cap">
            {isCollapsed ? "·" : group.label}
          </div>
          {group.entries.map((entry) => (
            <RailEntry key={entry.id} entry={entry} isCollapsed={isCollapsed} />
          ))}
        </React.Fragment>
      ))}

      {/* Sub-navigation of the inherited sections (admin pages…): rendered in
          the same visual vocabulary, only while the rail is expanded. */}
      {!isCollapsed &&
        subNavigation &&
        subNavigation.map((nav) => (
          <React.Fragment key={`nav-${nav.id}`}>
            {nav.label && <div className="compta-rail__cap">{nav.label}</div>}
            {nav.menus
              .filter(
                (menu) => !menu.featureFlag || hasFeature(menu.featureFlag)
              )
              .map((menu) => {
                const MenuIcon = menu.icon;
                return (
                  <a
                    key={menu.id}
                    href={menu.href}
                    target={menu.target}
                    className={cn(
                      "compta-rail__navi",
                      menu.current && "compta-rail__navi--active",
                      menu.disabled && "compta-rail__navi--disabled"
                    )}
                    aria-current={menu.current ? "page" : undefined}
                  >
                    <MenuIcon />
                    {menu.label}
                  </a>
                );
              })}
          </React.Fragment>
        ))}

      <div className="flex grow flex-col" />

      {!isCollapsed &&
        subscription.plan.code === FREE_TRIAL_PHONE_PLAN_CODE && (
          <div className="mx-3 mb-3">
            <TrialMessageUsage
              isAdmin={isAdmin(owner)}
              workspaceId={owner.sId}
            />
          </div>
        )}
      {user && !isCollapsed && (
        <div className="compta-rail__usermenu">
          <SidebarUserMenu
            user={user}
            owner={owner}
            subscription={subscription}
            isFairUseAwuLimitDisabled={hasFeature("disable_fair_use_awu_limit")}
          />
        </div>
      )}
    </div>
  );
});
